import ListItemButton from '@mui/material/ListItemButton';
import Typography from '@mui/material/Typography';
import TextLink from '@watershed/ui-core/components/TextLink';
import TruncatingTypographyWithTooltip from '@watershed/ui-core/components/TruncatingTypographyWithTooltip';
import { useEffect, useRef } from 'react';
import Stack from '@mui/material/Stack';

/**
 * One singular entry in the quick switcher
 */
export function QuickSwitcherItem({
  url,
  title,
  description,
  disabled,
  onClose,
  index,
  focus,
  hasChildItems,
  newTab,
}: {
  url: string;
  title: string;
  description?: string;
  disabled?: boolean;
  onClose: () => void;
  index: number;
  focus: boolean;
  hasChildItems: boolean;
  newTab?: boolean;
}) {
  const ref = useRef<HTMLLIElement>(null);

  // When this element is focused, scroll into view so if it's offscreen it becomes visible. Helpful with arrow keys to navigate these
  useEffect(() => {
    if (focus && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [focus]);

  const content = (
    <ListItemButton
      key={url}
      component="li"
      onClick={disabled ? undefined : onClose}
      tabIndex={0}
      ref={ref}
      aria-selected={focus}
      id={index.toString()}
      sx={(theme) => ({
        display: 'flex',
        gap: 2,
        padding: theme.spacing(2, 4),
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        '&:hover, &:focus, &[aria-selected="true"]': {
          backgroundColor: disabled
            ? 'transparent'
            : theme.palette.primary.light,
          color: disabled ? 'inherit' : theme.palette.primary.main,
        },
      })}
    >
      <Stack spacing={0.5} sx={{ flexGrow: 1 }}>
        <TruncatingTypographyWithTooltip
          tooltip={title}
          sx={{
            'a:hover &': {
              textDecoration: disabled ? 'none' : 'underline',
            },
            color: (theme) => theme.palette.cobalt,
          }}
        >
          {title}
        </TruncatingTypographyWithTooltip>
        {description && (
          <Typography
            variant="caption"
            sx={{
              color: (theme) => theme.palette.text.secondary,
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {description}
          </Typography>
        )}
      </Stack>
      {hasChildItems && (
        <Typography
          sx={(theme) => ({
            border: `1px solid ${theme.palette.grey50}`,
            borderRadius: 1,
            paddingY: 0.5,
            paddingX: 0.75,
            color: theme.palette.grey50,
          })}
          variant="caption"
        >
          Tab for more
        </Typography>
      )}
    </ListItemButton>
  );

  if (disabled) {
    return content;
  }

  return (
    <TextLink
      href={url}
      sx={{
        '&:hover': {
          // Overridden in child
          textDecoration: 'none',
        },
      }}
      newTab={newTab}
    >
      {content}
    </TextLink>
  );
}
